import type { SyntheticEvent } from "react";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getFormInputProps } from "util/forms";
import { selectCurrencies } from "features/general/selectors";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { ProofFileInput } from "ui/forms/formik-elements";
import When from "ui/support/When";
import OnboardingForm from "ui/forms/ECommerce/Onboarding/OnboardingForm";
import { OnboardingInputControl } from "ui/molecules/Input";
import { FormField, type OnboardingBankingAndFinanceProps } from "./types";

const BankingAndFinance = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    isIncomplete,
    requestedChangeFields,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: OnboardingBankingAndFinanceProps) => {
    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            handleSubmit={handleSubmit}
        >
            <Typography
                variant='h6'
                gutterBottom
            >
                Bank Account Details
            </Typography>
            <OnboardingInputControl
                name={FormField.AccountHolderName}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.AccountHolderName, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.AccountHolderName}
                        value={values[FormField.AccountHolderName]}
                        label='Account Holder Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.BankName}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.BankName, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.BankName}
                        value={values[FormField.BankName]}
                        label='Bank Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.Iban}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.Iban, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.Iban}
                        value={values[FormField.Iban]}
                        label='IBAN / Account Number'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.Swift}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.Swift, form)}
                        fullWidth
                        disabled={disabled}
                        size='small'
                        name={FormField.Swift}
                        value={values[FormField.Swift]}
                        label='SWIFT Code'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Proof of Bank Account Ownership*
            </Typography>
            <Typography
                variant='caption'
                gutterBottom
            >
                Recent bank statement (dated within the last 3 months) or bank account ownership letter.
            </Typography>
            <OnboardingInputControl
                name={FormField.Swift}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <ProofFileInput
                        multiple={false}
                        disabled={disabled}
                        name={FormField.ProofFileKey}
                        file={values[FormField.ProofFileKey]}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                What is your preferred currency?
            </Typography>
            <OnboardingInputControl
                name={FormField.Currency}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <StatefulSelector
                        fullWidth
                        disableClearable
                        disabled={disabled}
                        layout={null}
                        selectOptions={selectCurrencies}
                        value={values[FormField.Currency]}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.Currency, form),
                            onBlur: handleBlur,
                            required: true,
                            fullWidth: true,
                            name: FormField.Currency,
                            label: 'Preferred Currency'
                        }}
                        onChange={onAutocompleteChange(FormField.Currency)}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Financial Information
            </Typography>
            <OnboardingInputControl
                name={FormField.AvgTransactionSize}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.AvgTransactionSize, form)}
                        fullWidth
                        disabled={disabled}
                        type='number'
                        size='small'
                        name={FormField.AvgTransactionSize}
                        value={values[FormField.AvgTransactionSize]}
                        label='Average Transaction Size'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.MonthlyIncome}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.MonthlyIncome, form)}
                        fullWidth
                        disabled={disabled}
                        type='number'
                        size='small'
                        name={FormField.MonthlyIncome}
                        value={values[FormField.MonthlyIncome]}
                        label='Estimated Monthly Transaction Amount'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: (
                                <When
                                    if={values[FormField.Currency]}
                                    then={(
                                        <Typography
                                            variant='caption'
                                            color='textSecondary'
                                        >
                                            {values[FormField.Currency]}
                                        </Typography>
                                    )}
                                />
                            )
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.TransactionVolume}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.TransactionVolume, form)}
                        fullWidth
                        disabled={disabled}
                        type='number'
                        size='small'
                        name={FormField.TransactionVolume}
                        value={values[FormField.TransactionVolume]}
                        label='Expected Transaction Volume'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
        </OnboardingForm>
    );
};

export default BankingAndFinance;
